import React from 'react'
import { Box } from '@chakra-ui/react'
import { TikTokEmbed, YouTubeEmbed, FacebookEmbed, InstagramEmbed, XEmbed } from 'react-social-media-embed'
import { SOCIAL_EMBED_HOSTS, EMBED_URLS, MOBILE_BREAKPOINT, WWW_PREFIX, WWW_PREFIX_LENGTH } from 'constants/media'
export interface EmbedPreviewProps {
  url: string
  width?: number | string
  height?: number | string
  className?: string
}

export interface ResponsiveEmbedWrapperProps {
  url: string
  className?: string
}

function normalizeHost(url: string): string {
  try {
    const host = new URL(url).hostname.toLowerCase()
    return host.startsWith(WWW_PREFIX) ? host.slice(WWW_PREFIX_LENGTH) : host
  } catch {
    return ''
  }
}

function getYouTubeEmbedUrl(url: string): string {
  try {
    const videoIdMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)
    if (videoIdMatch && videoIdMatch[1]) {
      return `${EMBED_URLS.YOUTUBE.BASE}${videoIdMatch[1]}`
    }
    return url
  } catch {
    return url
  }
}

function getInstagramEmbedUrl(url: string): string {
  try {
    const postIdMatch = url.match(/instagram\.com\/p\/([^/?]+)/) || url.match(/instagram\.com\/reel\/([^/?]+)/)
    if (postIdMatch) {
      return `${EMBED_URLS.INSTAGRAM.BASE}${postIdMatch[1]}${EMBED_URLS.INSTAGRAM.EMBED_SUFFIX}`
    }
    return url
  } catch {
    return url
  }
}

function getTikTokEmbedUrl(url: string, isMobile: boolean = false): string {
  try {
    const videoIdMatch = url.match(/tiktok\.com\/.*\/video\/(\d+)/)
    const autoplayParam = isMobile ? '&autoplay=0' : ''
    
    if (videoIdMatch && videoIdMatch[1]) {
      return `${EMBED_URLS.TIKTOK.BASE}${videoIdMatch[1]}?${autoplayParam}`
    }
    if (EMBED_URLS.TIKTOK.SHORT_DOMAINS.some(domain => url.includes(domain))) {
      return `${EMBED_URLS.TIKTOK.BASE}?url=${encodeURIComponent(url)}${autoplayParam}`
    }
    if (url.includes('tiktok.com') && !url.includes('/embed/')) {
      return `${EMBED_URLS.TIKTOK.BASE}?url=${encodeURIComponent(url)}${autoplayParam}`
    }
    return url
  } catch {
    return url
  }
}

function getFacebookEmbedUrl(url: string, width: number = 400): string {
  try {
    const widthValue = Math.max(EMBED_URLS.FACEBOOK.MIN_WIDTH, Math.min(EMBED_URLS.FACEBOOK.MAX_WIDTH, width))
    const isVideo = url.includes('/video') || url.includes('/reel') || url.includes('/watch')
    const baseUrl = isVideo 
      ? EMBED_URLS.FACEBOOK.VIDEO_BASE
      : EMBED_URLS.FACEBOOK.BASE
    
    return `${baseUrl}?href=${encodeURIComponent(url)}&width=${widthValue}&show_text=true&height=${EMBED_URLS.FACEBOOK.DEFAULT_HEIGHT}&adapt_container_width=true`
  } catch {
    return url
  }
}

function getTwitterEmbedUrl(url: string): string {
  try {
    const tweetIdMatch = url.match(/(?:twitter\.com|x\.com)\/\w+\/status\/(\d+)/)
    if (tweetIdMatch && tweetIdMatch[1]) {
      const params = new URLSearchParams({
        id: tweetIdMatch[1],
        ...EMBED_URLS.TWITTER.DEFAULT_PARAMS
      })
      return `${EMBED_URLS.TWITTER.BASE}?${params.toString()}`
    }
    return url
  } catch {
    return url
  }
}

class EmbedErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Embed preview error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return <>{this.props.fallback}</>
    }
    return <>{this.props.children}</>
  }
}

export const EmbedPreview: React.FC<EmbedPreviewProps & { isMobile?: boolean }> = ({ url, width = '100%', height, className, isMobile = false }) => {
  const host = normalizeHost(url)

  const fallbackLink = (
    <a className={className} href={url} target="_blank" rel="noreferrer">
      {url}
    </a>
  )

  const renderEmbed = () => {
    if (SOCIAL_EMBED_HOSTS.YOUTUBE.includes(host)) {
      if (isMobile) {
        const embedUrl = getYouTubeEmbedUrl(url)
        return (
          <Box
            position="relative"
            width="100%"
            paddingBottom="56.25%"
            height={0}
            overflow="hidden"
            borderRadius="8px"
          >
            <Box
              as="iframe"
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              src={embedUrl}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
              style={{ pointerEvents: 'none' }}
            />
          </Box>
        )
      }
      return (
        <Box display="block" margin={0} padding={0}>
          <YouTubeEmbed url={url} width={400} />
        </Box>
      )
    }
    if (SOCIAL_EMBED_HOSTS.INSTAGRAM.includes(host)) {
      if (isMobile) {
        const embedUrl = getInstagramEmbedUrl(url)
        return (
          <Box
            width="100%"
            borderRadius="8px"
            overflow="hidden"
            position="relative"
            padding={0}
            margin={0}
            minHeight="350px"
          >
            <Box
              as="iframe"
              width="100%"
              src={embedUrl}
              frameBorder="0"
              scrolling="no"
              allow="encrypted-media"
              allowFullScreen
              loading="lazy"
              display="block"
              padding={0}
              margin={0}
              border="none"
              style={{
                minHeight: '350px',
                position: 'relative',
                top: 0,
                left: 0,
                verticalAlign: 'top',
                pointerEvents: 'none'
              }}
            />
          </Box>
        )
      }
      return (
        <Box display="block" margin={0} padding={0}>
          <InstagramEmbed url={url} width={400} />
        </Box>
      )
    }
    if (SOCIAL_EMBED_HOSTS.TIKTOK.includes(host)) {
      if (isMobile) {
        const embedUrl = getTikTokEmbedUrl(url, true)
        return (
          <Box
            width="100%"
            borderRadius="8px"
            overflow="hidden"
            position="relative"
            padding={0}
            margin={0}
            minHeight="700px"
          >
            <Box
              as="iframe"
              width="100%"
              src={embedUrl}
              frameBorder="0"
              scrolling="no"
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share; fullscreen"
              allowFullScreen
              loading="lazy"
              display="block"
              padding={0}
              margin={0}
              border="none"
              style={{
                position: 'relative',
                top: 0,
                left: 0,
                width: '100%',
                minHeight: '700px',
                verticalAlign: 'top',
                pointerEvents: 'none'
              }}
            />
          </Box>
        )
      }
      return (
        <Box display="block" margin={0} padding={0}>
          <TikTokEmbed url={url} width={325} />
        </Box>
      )
    }
    if (SOCIAL_EMBED_HOSTS.FACEBOOK.includes(host)) {
      if (isMobile) {
        const embedUrl = getFacebookEmbedUrl(url, 400)
        return (
          <Box
            width="100%"
            borderRadius="8px"
            overflow="hidden"
            position="relative"
            padding={0}
            margin={0}
          >
            <Box
              as="iframe"
              width="100%"
              src={embedUrl}
              frameBorder="0"
              scrolling="no"
              allow="encrypted-media; autoplay; fullscreen"
              allowFullScreen
              loading="lazy"
              display="block"
              padding={0}
              margin={0}
              border="none"
              style={{
                position: 'relative',
                top: 0,
                left: 0,
                verticalAlign: 'top',
                pointerEvents: 'none'
              }}
            />
          </Box>
        )
      }
      return (
        <Box display="block" margin={0} padding={0}>
          <FacebookEmbed url={url} width={400} />
        </Box>
      )
    }
    if (SOCIAL_EMBED_HOSTS.TWITTER.includes(host)) {
      if (isMobile) {
        const embedUrl = getTwitterEmbedUrl(url)
        return (
          <Box
            width="100%"
            borderRadius="8px"
            overflow="hidden"
            position="relative"
            padding={0}
            margin={0}
            minHeight="350px"
          >
            <Box
              as="iframe"
              width="100%"
              src={embedUrl}
              frameBorder="0"
              scrolling="no"
              allow="encrypted-media"
              allowFullScreen
              loading="lazy"
              display="block"
              padding={0}
              margin={0}
              border="none"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                verticalAlign: 'top',
                pointerEvents: 'none'
              }}
            />
          </Box>
        )
      }
      return (
        <Box display="block" margin={0} padding={0}>
          <XEmbed url={url} width={400} />
        </Box>
      )
    }

    return fallbackLink
  }

  return (
    <EmbedErrorBoundary fallback={fallbackLink}>
      {renderEmbed()}
    </EmbedErrorBoundary>
  )
}

export const ResponsiveEmbedWrapper: React.FC<ResponsiveEmbedWrapperProps> = ({ url, className }) => {
  const [isMobile, setIsMobile] = React.useState(false)

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }

    checkMobile()

    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <Box
      width="100%"
      margin={0}
      padding={0}
      position="relative"
      className={className}
      style={{ isolation: 'isolate' }}
    >
      <EmbedPreview url={url} isMobile={isMobile} />
      {isMobile && (
        <Box
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          zIndex={10}
          cursor="pointer"
          onClick={handleClick}
          style={{
            backgroundColor: 'transparent',
            touchAction: 'manipulation'
          }}
        />
      )}
    </Box>
  )
}

export default EmbedPreview


